import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Home, MapPin, Phone, ArrowLeft, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import AdmissionsTab from '../components/AdmissionsTab';
import { api } from '../api';

function Star({ r, label }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
      <div className="mt-1 text-[18px] font-semibold text-[#A06628] tabular-nums">
        {r != null ? <>{Number(r).toFixed(1)}<span className="text-[#D4A85C] ml-1">★</span></> : <span className="text-[#A8B0AA] text-[14px]">no rating</span>}
      </div>
    </div>
  );
}

function ServiceCard({ row }) {
  const items = [
    ['offers_nursing', 'Nursing care'],
    ['offers_pt', 'Physical therapy'],
    ['offers_ot', 'Occupational therapy'],
    ['offers_slp', 'Speech-language pathology'],
    ['offers_msw', 'Medical social services'],
    ['offers_hha', 'Home health aide'],
  ];
  return (
    <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
      <div className="text-[11px] uppercase tracking-wider text-[#4A7C5E] font-semibold mb-3">Services offered</div>
      <ul className="space-y-1.5">
        {items.map(([k, label]) => (
          <li key={k} className="flex items-center justify-between text-[13px]">
            <span className="text-[#3A4A44]">{label}</span>
            {row[k]
              ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-[#E5F0EA] text-[#2D5F4F]">YES</span>
              : <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-[#F5EFDD] text-[#A8B0AA]">—</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HomeHealthDetailPage() {
  const { ccn } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    setLoading(true);
    api.homeHealthAgency(ccn).then((d) => {
      setA(d); setLoading(false);
      try {
        window.gtag && window.gtag('event', 'profile_view', {
          entity_type: 'home_health', ccn: d?.ccn, state: d?.state,
          rating: d?.quality_rating || null,
        });
      } catch (_) {}
    }).catch(() => setLoading(false));
  }, [ccn]);

  if (loading) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading agency…</div></main></div>;
  if (!a) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">Agency not found</h1></main></div>;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Home Health', to: '/home-health' }, { label: a.name }]} />

        <header className="mt-2 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#E5F0EA] text-[#2D5F4F]">
              <Home size={11} /> Home Health Agency · CCN {a.ccn}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1A2622] mt-2">{a.name}</h1>
            <div className="mt-2 flex flex-wrap gap-3 text-[12.5px] text-[#5A6660]">
              {a.address && <span className="flex items-center gap-1"><MapPin size={12} /> {a.address}, {a.city}, {a.state} {a.zip}</span>}
              {a.phone && <span className="flex items-center gap-1"><Phone size={12} /> {a.phone}</span>}
            </div>
          </div>
          <Link to="/home-health" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-[#E5DCC4] text-[#3A4A44] text-[12.5px] hover:bg-[#F5EFDD]">
            <ArrowLeft size={13} /> All agencies
          </Link>
        </header>

        <div className="border-b border-[#E5DCC4] flex gap-1" data-testid="hha-tabs">
          {[{id:'overview',label:'Overview'},{id:'admissions',label:'Admissions Activity'}].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hha-tab-${t.id}`}
              className={`px-4 h-10 text-[12.5px] font-medium border-b-2 -mb-px transition ${tab === t.id ? 'border-[#4A7C5E] text-[#1A2622]' : 'border-transparent text-[#7A857F] hover:text-[#1A2622]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Star r={a.quality_rating} label="Quality of patient care" />
              <Star r={a.survey_rating} label="Patient survey (HHCAHPS)" />
              <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
                <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Ownership</div>
                <div className="mt-1 text-[13px] text-[#1A2622]">{a.ownership || '—'}</div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
                <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Certified</div>
                <div className="mt-1 text-[13px] text-[#1A2622]">{a.certification_date || '—'}</div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ServiceCard row={a} />
              <div className="p-5 rounded-xl bg-[#1A2622] text-[#FBF6EC]">
                <div className="text-[11px] uppercase tracking-wider text-[#D4A85C] font-semibold mb-3">In the post-acute network</div>
                <p className="text-[13px] opacity-90 leading-relaxed">
                  This agency is indexed alongside <strong className="text-white">{a.zip ? `ZIP ${a.zip}` : 'the local area'}</strong> SNFs, hospitals and hospices.
                  Visit any nearby <Link to="/facilities" className="underline text-[#D4A85C]">SNF</Link> or <Link to="/hospitals" className="underline text-[#D4A85C]">hospital</Link> profile and open the
                  <strong className="text-white"> Post-Acute Network</strong> tab to see this agency surface as a discharge partner.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
              <Info size={12} className="mt-0.5 shrink-0" />
              <span><strong>Source:</strong> {a.source}. Public CMS data only — no PHI.</span>
            </div>
          </>
        )}

        {tab === 'admissions' && <AdmissionsTab kind="hha" ccn={a.ccn} />}
      </main>
      <Footer />
    </div>
  );
}
